import { useState, useEffect, useMemo } from 'react';

/**
 * Universal Text Sizing System for Judge Station
 * 
 * This provides consistent text sizing across all components on the judge station page,
 * based on the overall viewport/container size rather than individual component dimensions.
 * This ensures visual cohesion and readability across all cards and interfaces.
 */

// Hook to detect overall screen/viewport size for consistent text sizing
export const useUniversalTextSizing = () => {
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Universal sizing based on viewport dimensions
  const textSize = useMemo(() => {
    const { width, height } = windowDimensions;
    
    // Determine size based on viewport dimensions for consistent text sizing
    if (width < 768 || height < 600) {
      return 'small';
    } else if (width < 1024 || height < 768) {
      return 'medium';
    } else if (width < 1440 || height < 900) {
      return 'large';
    } else {
      return 'xlarge';
    }
  }, [windowDimensions]);

  return { textSize, windowDimensions };
};

// Comprehensive text sizing configurations for all judge station elements
// Refined for a more professional, compact appearance
export const UNIVERSAL_TEXT_SIZES = {
  small: {
    // Card titles and headers
    cardTitle: 'text-2xs',
    cardSubtitle: 'text-3xs',
    
    // Content text
    bodyText: 'text-2xs',
    smallText: 'text-3xs',
    captionText: 'text-3xs',
    
    // Interactive elements
    buttonText: 'text-2xs',
    inputText: 'text-2xs',
    labelText: 'text-3xs',
    
    // Scores and numbers
    scoreDisplay: 'text-xl',
    scoreLabel: 'text-3xs',
    numberInput: 'text-xs',
    
    // Icons and spacing
    iconSize: 'w-3 h-3',
    iconSizeLarge: 'w-3.5 h-3.5',
    badgeSize: 'text-3xs px-1 py-0.25',
    spacing: 'gap-0.5',
    spacingLarge: 'gap-1',
    
    // Padding and margins
    cardPadding: 'p-1.5',
    sectionPadding: 'p-1',
    buttonPadding: 'px-1 py-0.5',
    
    // Heights
    buttonHeight: 'h-5',
    inputHeight: 'h-6'
  },
  medium: {
    // Card titles and headers
    cardTitle: 'text-xs',
    cardSubtitle: 'text-2xs',
    
    // Content text
    bodyText: 'text-xs',
    smallText: 'text-2xs',
    captionText: 'text-2xs',
    
    // Interactive elements
    buttonText: 'text-xs',
    inputText: 'text-xs',
    labelText: 'text-2xs',
    
    // Scores and numbers
    scoreDisplay: 'text-2xl',
    scoreLabel: 'text-2xs',
    numberInput: 'text-sm',
    
    // Icons and spacing
    iconSize: 'w-3.5 h-3.5',
    iconSizeLarge: 'w-4 h-4',
    badgeSize: 'text-2xs px-1.5 py-0.25',
    spacing: 'gap-1',
    spacingLarge: 'gap-1.5',
    
    // Padding and margins
    cardPadding: 'p-2',
    sectionPadding: 'p-1.5',
    buttonPadding: 'px-1.5 py-0.5',
    
    // Heights
    buttonHeight: 'h-6',
    inputHeight: 'h-7'
  },
  large: {
    // Card titles and headers
    cardTitle: 'text-sm',
    cardSubtitle: 'text-xs',
    
    // Content text
    bodyText: 'text-xs',
    smallText: 'text-2xs',
    captionText: 'text-2xs',
    
    // Interactive elements
    buttonText: 'text-xs',
    inputText: 'text-xs',
    labelText: 'text-2xs',
    
    // Scores and numbers
    scoreDisplay: 'text-3xl',
    scoreLabel: 'text-2xs',
    numberInput: 'text-base',
    
    // Icons and spacing
    iconSize: 'w-4 h-4',
    iconSizeLarge: 'w-5 h-5',
    badgeSize: 'text-2xs px-2 py-0.25',
    spacing: 'gap-1.5',
    spacingLarge: 'gap-2',
    
    // Padding and margins
    cardPadding: 'p-3',
    sectionPadding: 'p-2',
    buttonPadding: 'px-2 py-1',
    
    // Heights
    buttonHeight: 'h-7',
    inputHeight: 'h-8'
  },
  xlarge: {
    // Card titles and headers
    cardTitle: 'text-base',
    cardSubtitle: 'text-sm',
    
    // Content text
    bodyText: 'text-sm',
    smallText: 'text-xs',
    captionText: 'text-xs',
    
    // Interactive elements
    buttonText: 'text-sm',
    inputText: 'text-sm',
    labelText: 'text-xs',
    
    // Scores and numbers
    scoreDisplay: 'text-4xl',
    scoreLabel: 'text-xs',
    numberInput: 'text-lg',
    
    // Icons and spacing
    iconSize: 'w-5 h-5',
    iconSizeLarge: 'w-6 h-6',
    badgeSize: 'text-xs px-2.5 py-0.5',
    spacing: 'gap-2',
    spacingLarge: 'gap-3',
    
    // Padding and margins
    cardPadding: 'p-4',
    sectionPadding: 'p-3',
    buttonPadding: 'px-3 py-1.5',
    
    // Heights
    buttonHeight: 'h-8',
    inputHeight: 'h-10'
  }
};

// Helper function to get text configuration
export const getUniversalTextConfig = (textSize) => {
  return UNIVERSAL_TEXT_SIZES[textSize] || UNIVERSAL_TEXT_SIZES.medium;
};

// Backward compatibility - keep existing exports for components already using them
export const useUniversalCardTitleSize = () => {
  const { textSize, windowDimensions } = useUniversalTextSizing();
  return { titleSize: textSize, windowDimensions };
};

export const UNIVERSAL_CARD_TITLE_SIZES = {
  small: {
    titleSize: UNIVERSAL_TEXT_SIZES.small.cardTitle,
    iconSize: UNIVERSAL_TEXT_SIZES.small.iconSize,
    badgeSize: UNIVERSAL_TEXT_SIZES.small.badgeSize,
    spacing: UNIVERSAL_TEXT_SIZES.small.spacing
  },
  medium: {
    titleSize: UNIVERSAL_TEXT_SIZES.medium.cardTitle,
    iconSize: UNIVERSAL_TEXT_SIZES.medium.iconSize,
    badgeSize: UNIVERSAL_TEXT_SIZES.medium.badgeSize,
    spacing: UNIVERSAL_TEXT_SIZES.medium.spacing
  },
  large: {
    titleSize: UNIVERSAL_TEXT_SIZES.large.cardTitle,
    iconSize: UNIVERSAL_TEXT_SIZES.large.iconSize,
    badgeSize: UNIVERSAL_TEXT_SIZES.large.badgeSize,
    spacing: UNIVERSAL_TEXT_SIZES.large.spacing
  },
  xlarge: {
    titleSize: UNIVERSAL_TEXT_SIZES.xlarge.cardTitle,
    iconSize: UNIVERSAL_TEXT_SIZES.xlarge.iconSize,
    badgeSize: UNIVERSAL_TEXT_SIZES.xlarge.badgeSize,
    spacing: UNIVERSAL_TEXT_SIZES.xlarge.spacing
  }
};

export const getUniversalTitleConfig = (titleSize) => {
  return UNIVERSAL_CARD_TITLE_SIZES[titleSize] || UNIVERSAL_CARD_TITLE_SIZES.medium;
};